"use client";

import Link from "next/link";
import { ArrowRight, Cpu, Brain, Network, Sparkles, Heart, Target, Globe } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { relatedCapabilities as data } from "@/lib/data/capabilities-narrative-engineering";

const capIcons = [Sparkles, Heart, Target, Globe];
const engineIcons = [Cpu, Brain, Network];

const capAccents = [
  "var(--color-crimson)",
  "var(--color-rose)",
  "var(--color-denim)",
  "var(--color-purple)",
] as const;

const engineAccents = [
  "var(--color-cyan)",
  "var(--color-pink)",
  "var(--color-emerald)",
] as const;

export function RelatedCapabilities() {
  return (
    <Section id="related-capabilities" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12">
            <div className="eyebrow-tiny text-[var(--color-crimson)] mb-4">Related Capabilities</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.capabilities.map((c, i) => {
                const Icon = capIcons[i % capIcons.length]!;
                const accent = capAccents[i % capAccents.length]!;
                return (
                  <Link
                    key={c.name}
                    href={c.href}
                    className="glass rounded-2xl p-5 h-full block hover:scale-[1.01] transition-transform"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                    }}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: accent }} />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold">{c.name}</h3>
                          <ArrowRight className="h-3.5 w-3.5" style={{ color: accent }} />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={220}>
          <div className="mt-10">
            <div className="eyebrow-tiny text-[var(--color-amber)] mb-4">Related Engines</div>
            <div className="grid sm:grid-cols-3 gap-4">
              {data.engines.map((e, i) => {
                const Icon = engineIcons[i % engineIcons.length]!;
                const accent = engineAccents[i % engineAccents.length]!;
                return (
                  <Link
                    key={e.name}
                    href={e.href}
                    className="glass rounded-2xl p-5 h-full block hover:scale-[1.01] transition-transform"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5 mb-2" style={{ color: accent }} />
                    <h3 className="text-sm font-bold">{e.name}</h3>
                    <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{e.body}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
